import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import AdSense from './AdSense';
import { sanitizeErrorMessage, sanitizeFilename, validateFileSize } from '../utils/security';

// Set to true when you integrate Google AdSense
const SHOW_ADS = true;

const FileConverter = () => {
  const [conversionType, setConversionType] = useState('pdf-to-word');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadFileName, setDownloadFileName] = useState('');

  // SECURITY: Maximum file size (50MB) - matches backend limit
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const conversionOptions = [
    { value: 'pdf-to-word', label: 'PDF → Word', icon: '📄→📝', from: 'PDF', to: 'DOCX' },
    { value: 'excel-to-pdf', label: 'Excel → PDF', icon: '📊→📄', from: 'XLSX/XLS', to: 'PDF' },
    { value: 'image-to-pdf', label: 'Image → PDF', icon: '🖼️→📄', from: 'JPG/PNG/GIF', to: 'PDF' },
    { value: 'word-to-pdf', label: 'Word → PDF', icon: '📝→📄', from: 'DOCX/DOC', to: 'PDF' },
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // SECURITY: Client-side file size validation
      const sizeCheck = validateFileSize(selectedFile, 50);
      if (!sizeCheck.valid) {
        setError(sizeCheck.error);
        setFile(null);
        setFileName('');
        setDownloadUrl('');
        // Reset file input
        e.target.value = '';
        return;
      }

      // SECURITY: Sanitize filename for display (prevent XSS)
      const sanitizedFileName = sanitizeFilename(selectedFile.name);
      
      setFile(selectedFile);
      setFileName(sanitizedFileName);
      setError('');
      setDownloadUrl('');
    }
  };

  const handleConvert = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError('');
    setDownloadUrl('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = `${API_URL}/convert/${conversionType}/`;
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // SECURITY: Revoke previous blob URL if exists (prevent memory leak)
        if (downloadUrl) {
          URL.revokeObjectURL(downloadUrl);
        }

        // Create download URL from base64 data
        const byteCharacters = atob(response.data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: response.data.mime_type });
        const url = URL.createObjectURL(blob);
        
        setDownloadUrl(url);
        // SECURITY: Sanitize filename from server
        const sanitizedFilename = sanitizeFilename(response.data.filename || 'download');
        setDownloadFileName(sanitizedFilename);
      } else {
        // SECURITY: Sanitize error message
        setError(sanitizeErrorMessage(response.data.error || 'Conversion failed'));
      }
    } catch (err) {
      // SECURITY: Sanitize error message
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred during conversion. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      // SECURITY: Sanitize download filename
      link.download = sanitizeFilename(downloadFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // SECURITY: Revoke blob URL after download (cleanup)
      // Use setTimeout to ensure download starts before revoking
      setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
        setDownloadUrl('');
      }, 100);
    }
  };

  // SECURITY: Cleanup blob URL on component unmount
  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const getAcceptedFileTypes = () => {
    switch (conversionType) {
      case 'pdf-to-word':
        return '.pdf';
      case 'excel-to-pdf':
        return '.xlsx,.xls';
      case 'image-to-pdf':
        return '.jpg,.jpeg,.png,.gif,.bmp';
      case 'word-to-pdf':
        return '.docx,.doc';
      default:
        return '*';
    }
  };

  const selectedOption = conversionOptions.find(opt => opt.value === conversionType);

  return (
    <div className="viewer-container">
      <div className="converter-header">
        <h2>File Converter</h2>
        <p className="converter-subtitle">Convert files between different formats instantly</p>
      </div>

      <div className="converter-options">
        <h3>Select Conversion Type</h3>
        <div className="conversion-grid">
          {conversionOptions.map((option) => (
            <button
              key={option.value}
              className={`conversion-option ${conversionType === option.value ? 'active' : ''}`}
              onClick={() => {
                // SECURITY: Cleanup blob URL when changing conversion type
                if (downloadUrl) {
                  URL.revokeObjectURL(downloadUrl);
                }
                setConversionType(option.value);
                setFile(null);
                setFileName('');
                setError('');
                setDownloadUrl('');
              }}
            >
              <span className="conversion-icon">{option.icon}</span>
              <span className="conversion-label">{option.label}</span>
              <span className="conversion-details">{option.from} → {option.to}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="converter-upload">
        <h3>Upload File</h3>
        <div className="file-upload-area">
          <input
            type="file"
            id="file-input"
            accept={getAcceptedFileTypes()}
            onChange={handleFileChange}
            className="file-input"
          />
          <label htmlFor="file-input" className="file-upload-label">
            <span className="upload-icon">📁</span>
            <span className="upload-text">
              {fileName || `Click to select ${selectedOption?.from} file`}
            </span>
          </label>
          {fileName && (
            <div className="file-info">
              <span className="file-name">✓ {fileName}</span>
              <button 
                className="remove-file-btn"
                onClick={() => {
                  // SECURITY: Cleanup blob URL when removing file
                  if (downloadUrl) {
                    URL.revokeObjectURL(downloadUrl);
                  }
                  setFile(null);
                  setFileName('');
                  setError('');
                  setDownloadUrl('');
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="converter-actions">
        <button
          onClick={handleConvert}
          disabled={!file || loading}
          className="convert-btn"
        >
          {loading ? 'Converting...' : `Convert to ${selectedOption?.to}`}
        </button>
      </div>

      {downloadUrl && (
        <>
          <div className="converter-result">
            <div className="success-message">
              ✓ Conversion successful! Your file is ready to download.
            </div>
            <button onClick={handleDownload} className="download-btn">
              📥 Download {downloadFileName}
            </button>
          </div>
          
          {/* In-content Ad - Only visible when SHOW_ADS is true and conversion is successful */}
          {SHOW_ADS && (
            <div className="in-content-ad">
              <AdSense
                adFormat="auto"
                style={{ width: '100%', maxWidth: '728px', height: '90px', margin: '2rem auto', display: 'block' }}
                className="ad-incontent"
              />
            </div>
          )}
        </>
      )}

      <div className="converter-info">
        <h4>Supported Formats</h4>
        <ul>
          <li><strong>PDF to Word:</strong> Convert PDF documents to editable Word format</li>
          <li><strong>Excel to PDF:</strong> Convert Excel spreadsheets to PDF documents</li>
          <li><strong>Image to PDF:</strong> Convert JPG, PNG, GIF images to PDF</li>
          <li><strong>Word to PDF:</strong> Convert Word documents to PDF format</li>
        </ul>
        <p className="info-note">
          <strong>Note:</strong> File conversions are processed securely. Files are not stored on our servers.
        </p>
      </div>
    </div>
  );
};

export default FileConverter;

